<template>
  <daily-prices
    :propBaseProducts.sync="baseProducts"
    :propProductsToShow="productsToShow"
    :propFxSettlement.sync="fxSettlement"
    :propErrorMessage="error"
    :settlementRulesValid="areSettlementRulesValid"
    :hasExistingSettlementValues.sync="hasExistingSettlementValues"
    @on-price-change="onPriceChange"
    @save-settlements="savePricesMethod"
  />
</template>
<script>
import DailyPrices from "./DailyPricesTemplate.vue";

import { mapActions, mapGetters } from "vuex";
import { cloneDeep } from "lodash";

import { createPricingRules, updatePricingRules } from "@/api/pricingRules";
import { rulesEnum } from "@/util/enums/rules";
import {
  getBaseSettlementId,
  getStraightProductId,
  getBioProductId,
  getProductDensityReferenceId,
  getProductDutyReferenceId,
} from "@/util/products";
import { calculateBlendedFlatPrice, calculateFlatPrice, calculateLivePrice, getIceSymbol } from "@/util/calculations";

export default {
  components: {
    DailyPrices,
  },
  data: function () {
    return {
      settlementRules: [],
      productRules: [],
      iceDataRules: [],
      hasExistingSettlementValues: false
    };
  },
  created: async function () {
    await this.getMetadataConfig();
    this.populateRules();
    this.checkForExistingSettlementValues();
  },
  computed: {
    ...mapGetters("rules", ["globalRulesGetter"]),
    ...mapGetters("metadata", ["productConfig", "blendedProductConfig"]),
    ...mapGetters("fuelsType", ["getFuelsType"]),
    ...mapGetters("livePrices", ["getPricesUpdate"]),
    fxSettlementRule: function () {
      return this.settlementRules.find((r) => r.action.targetAttribute === rulesEnum.eTargetAttributeType.Fx);
    },
    fxSettlement: {
      get: function () {
        return this.fxSettlementRule ? this.fxSettlementRule.action.value : "";
      },
      set: function (newVal) {
        if (this.fxSettlementRule) this.fxSettlementRule.action.value = newVal;
      },
    },
    fxLive: function () {
      const fxLiveObject = this.getPricesUpdate.find((p) => p.symbol === "#X.SGBPUSD");
      return fxLiveObject ? fxLiveObject.price : 0;
    },
    iceSettle: function () {
      let iceSettleValue = 0;
      const iceSettleMonthRule = this.getRuleByTargetAttribute(
        this.iceDataRules,
        rulesEnum.eTargetAttributeType.IceSettle
      );
      if (iceSettleMonthRule) {
        const iceSettleSymbol = getIceSymbol(iceSettleMonthRule.action.value);
        iceSettleValue = iceSettleSymbol ? this.getPricesUpdate.find((p) => p.symbol === iceSettleSymbol).settle : 0;
      }
      return iceSettleValue;
    },
    iceLive: function () {
      let iceLiveValue = 0;
      const iceLiveRule = this.getRuleByTargetAttribute(this.iceDataRules, rulesEnum.eTargetAttributeType.IceLive);
      if (iceLiveRule) {
        const iceLiveSymbol = getIceSymbol(iceLiveRule.action.value);
        iceLiveValue = iceLiveSymbol ? this.getPricesUpdate.find((p) => p.symbol === iceLiveSymbol).price : 0;
      }
      return iceLiveValue;
    },
    iceLivePremium: function () {
      const iceLivePremiumRule = this.getRuleByTargetAttribute(
        this.iceDataRules,
        rulesEnum.eTargetAttributeType.IceLivePremium
      );
      return iceLivePremiumRule ? Number(iceLivePremiumRule.action.value) : 0;
    },
    bioBlend: function () {
      const { BioBlend } = rulesEnum.eTargetAttributeType;
      const bioBlendRule = this.getRuleByTargetAttribute(this.productRules, BioBlend);
      return bioBlendRule ? Number(bioBlendRule.action.value) / 100 : 0;
    },
    baseProducts: function () {
      const { DieselId, KeroseneId, BioDieselId } = this.productConfig;
      const data = this.mapProductsWithSettlementPrice(this.getFuelsType);
      return [
        data.find((product) => product.fuel_id === DieselId),
        data.find((product) => product.fuel_id === KeroseneId),
        data.find((product) => product.fuel_id === BioDieselId),
      ]
    },
    additionalProducts: function () {
      const { GasoilId, BioDieselForGasoilId, BlendedGasoilId, BlendedDieselBioId } = this.productConfig;
      const data = this.mapProductsWithSettlementPrice(this.getFuelsType);
      return [
        data.find((product) => product.fuel_id === GasoilId),
        data.find((product) => product.fuel_id === BioDieselForGasoilId),
        data.find((product) => product.fuel_id === BlendedGasoilId),
        data.find((product) => product.fuel_id === BlendedDieselBioId),
      ]
    },
    productsToShow: function () {
      const { DieselId, BioDieselId, BlendedDieselBioId, GasoilId, BlendedGasoilId, KeroseneId } = this.productConfig;
      const allProducts = [...this.baseProducts, ...this.additionalProducts];
      const allProductsWithPricingDetails = this.calculatePricingDetails(allProducts);
      return [
        allProductsWithPricingDetails.find((product) => product.fuel_id === DieselId),
        allProductsWithPricingDetails.find((product) => product.fuel_id === BioDieselId),
        allProductsWithPricingDetails.find((product) => product.fuel_id === BlendedDieselBioId),
        allProductsWithPricingDetails.find((product) => product.fuel_id === GasoilId),
        allProductsWithPricingDetails.find((product) => product.fuel_id === BlendedGasoilId),
        allProductsWithPricingDetails.find((product) => product.fuel_id === KeroseneId),
      ];
    },
    error: function () {
      const isInvalidBaseSettlements = this.baseProducts.some(({ price }) => price === "" );
      if (this.fxSettlement === "" || isInvalidBaseSettlements) {
        return "Please enter all four settlement prices that are above zero";
      }
      return "";
    },
    areSettlementRulesValid() {
      return this.error === "";
    },
  },
  methods: {
    ...mapActions("metadata", ["getMetadataConfig"]),
    ...mapActions("rules", ["getGlobalRules"]),
    ...mapActions("livePrices", ["getSellerProductPrices"]),
    populateRules: function () {
      this.settlementRules = cloneDeep(this.globalRulesGetter.dailySettlementRules);
      this.iceDataRules = cloneDeep(this.globalRulesGetter.iceDataRules);
      this.productRules = cloneDeep(Object.values(this.globalRulesGetter.productRules).flatMap(rule => rule));
    },
    checkForExistingSettlementValues() {
      const isInvalidBaseSettlements = this.baseProducts.some(({ price }) => price === "");
      this.hasExistingSettlementValues = this.fxSettlement !== '' && !isInvalidBaseSettlements
    },
    getFuelPriceRule: function (fuelId) {
      return this.settlementRules.find(
        (r) => r.targets[0] && r.targets[0].type === rulesEnum.eTargetType.Product && r.targets[0].ids.includes(fuelId)
      );
    },
    getRuleByTargetAttributeAndFuelId(rules, targetAttribute, fuelId) {
      return rules.find((r) => r.action.targetAttribute === targetAttribute && r.targets[0].ids.includes(fuelId));
    },
    getRuleByTargetAttribute: function (rules, targetAttribute) {
      return rules.find((rule) => rule.action.targetAttribute === targetAttribute);
    },
    mapProductsWithSettlementPrice(products) {
      const { ProductSettle } = rulesEnum.eTargetAttributeType;
      return products
        .map((f) => {
          const fuelPriceRule = this.getRuleByTargetAttributeAndFuelId(this.settlementRules, ProductSettle, f._id);
          return {
            name: f.name,
            fuel_id: f._id,
            price: fuelPriceRule ? fuelPriceRule.action.value : "",
          };
        });
    },
    calculatePricingDetails: function (products) {
      const fxSettlement = Number(this.fxSettlement);
      let { totalFixed, usdDiff, marketChange, totalLive } = 0;
      const { ProductSettle, Density, Duty } = rulesEnum.eTargetAttributeType;
      const productsWithPricingDetails = [];

      for (const product of products) {
        const productId = product.fuel_id;
        const isBlended = this.blendedProductConfig.includes(productId);
        if (isBlended) {
          // Calculate this product's settle/flat price using its base product's and bio product's settle prices
          const straightProduct = productsWithPricingDetails.find(
            ({ fuel_id }) => fuel_id === getStraightProductId(this.productConfig, productId)
          );
          const bioProduct = productsWithPricingDetails.find(
            ({ fuel_id }) => fuel_id === getBioProductId(this.productConfig, productId)
          );

          // Calculate this product's market change using its base product's and bio product's market changes
          totalFixed = calculateBlendedFlatPrice(this.bioBlend, bioProduct.totalFixed, straightProduct.totalFixed);
          marketChange = calculateBlendedFlatPrice(
            this.bioBlend,
            bioProduct.marketChange,
            straightProduct.marketChange
          );
        } else {
          // get the base product's settlement
          const baseSettlementId = getBaseSettlementId(this.productConfig, productId);
          const baseSettlementRule = this.getRuleByTargetAttributeAndFuelId(
            this.settlementRules,
            ProductSettle,
            baseSettlementId
          );
          const baseProductSettlement = baseSettlementRule ? Number(baseSettlementRule.action.value) : 0;

          // Get the product's density
          const productDensityRefId = getProductDensityReferenceId(this.productConfig, productId);
          const densityRule = this.getRuleByTargetAttributeAndFuelId(this.productRules, Density, productDensityRefId);
          const density = densityRule ? Number(densityRule.action.value) : 0;

          // Get the product's duty
          const productDutyRefId = getProductDutyReferenceId(this.productConfig, productId);
          const dutyRule = this.getRuleByTargetAttributeAndFuelId(this.productRules, Duty, productDutyRefId);
          const duty = dutyRule ? Number(dutyRule.action.value) : 0;

          // Calculate settle/flat price for this product
          totalFixed = calculateFlatPrice(baseProductSettlement, fxSettlement, density, duty);
          totalFixed = Number.isFinite(totalFixed) ? Number(totalFixed) : 0;

          // Calculate differential in USD
          usdDiff = baseProductSettlement - this.iceSettle;
          usdDiff = Number.isFinite(usdDiff) ? Number(usdDiff) : 0;
          // Calculate market change
          marketChange = calculateFlatPrice(this.iceLive + usdDiff, this.fxLive, density, duty) - totalFixed;
          marketChange = Number.isFinite(marketChange) ? Number(marketChange) : 0;
        }
        totalLive = calculateLivePrice(totalFixed, marketChange);
        productsWithPricingDetails.push({ ...product, totalFixed, marketChange, totalLive });
      }
      return productsWithPricingDetails;
    },
    onPriceChange: function (productId, newValue) {
      const { ProductSettle } = rulesEnum.eTargetAttributeType;
      const fuelPriceRule = this.getRuleByTargetAttributeAndFuelId(this.settlementRules, ProductSettle, productId);
      if (fuelPriceRule) fuelPriceRule.action.value = newValue;
    },
    savePricesMethod: async function (onSaveCallback) {
      const allRules = cloneDeep([...this.settlementRules, ...this.productRules, ...this.iceDataRules]);
      for (const rule of allRules) {
        const { value } = rule.action;
        rule.action.value = Number(value);
      }
      const newRules = allRules.filter(r => !r._id);
      const updatedRules = allRules.filter(r => r._id).map(r => { return { ...r, ruleId: r._id } });
      try {
          if (newRules.length > 0) await createPricingRules(newRules);
          if (updatedRules.length > 0) await updatePricingRules(updatedRules);
          await this.getGlobalRules();
          const dailySettlement = this.$store.getters["rules/globalRulesGetter"].dailySettlementRules;
          await this.getSellerProductPrices({ dailySettlement });
          this.populateRules();
          onSaveCallback(true);
      } catch(err) {
        onSaveCallback(false);
      }
    },
  },
};
</script>

<style></style>
